<!-- Updated NewsView.vue -->
<template>
  <div class="news">
    <!-- News Header -->
    <div class="jumbotron-fluid bg-ctdark py-4">
      <div class="container">
        <div class="row">
          <div class="col-md-12 col-lg-4"></div>
          <div class="col-md-12 col-lg-4 text-center pb-2">
            <h1 class="text-blog-title text-light pt-0">{{ t('news.title') }}</h1>
          </div>
          <div class="col-md-12 col-lg-4"></div>
        </div>
      </div>
    </div>

    <!-- News Content -->
    <div class="jumbotron-fluid py-3 bg-ctmain">
      <div class="container">
        <div class="row">
          <!-- News Entries Column -->
          <div class="col-md-8 mt-4 left">
            <div v-if="loading" class="text-center p-5">
              <div class="spinner-border text-ctorange" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading news items...</p>
            </div>

            <div v-else-if="error" class="alert alert-danger">
              <p><strong>Error loading news:</strong></p>
              <pre>{{ JSON.stringify(error, null, 2) }}</pre>
            </div>

            <div v-else-if="!newsItems || newsItems.length === 0" class="text-center p-5">
              <p>{{ t('news.emptyMessage') }}</p>
              <button @click="retryFetch" class="btn btn-ctl mt-3">{{ t('news.retry') }}</button>
            </div>

            <div v-else>
              <div v-for="item in newsItems" :key="item.id" class="card mb-4">
                <div class="card-body">
                  <template v-if="item.status === 0">
                    <h3 class="card-title font-italic text-secondary">
                      (draft)
                      <router-link :to="{ name: 'news-item', params: { slug: item.slug } }" class="text-secondary">
                        {{ getTranslatedTitle(item) }}
                      </router-link>
                    </h3>
                  </template>
                  <template v-else>
                    <h3 class="card-title">
                      <router-link :to="{ name: 'news-item', params: { slug: item.slug } }" class="text-decoration-none text-white">
                        {{ getTranslatedTitle(item) }}
                      </router-link>
                    </h3>
                  </template>
                  <p class="card-text text-muted h6">{{ formatDate(item.created_on) }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="col-md-4 float-right">
            <div class="card my-4">
              <h5 class="card-header">{{ t('news.recentNews') }}</h5>
              <div v-if="newsItems && newsItems.length > 0">
                <div v-for="item in recentItems" :key="item.id" class="card-body py-2">
                  <p class="card-text">
                    <router-link :to="{ name: 'news-item', params: { slug: item.slug } }" class="text-decoration-none text-white">
                      {{ getTranslatedTitle(item) }}
                    </router-link>
                    <span class="font-italic small ms-2 text-muted">{{ formatDate(item.created_on) }}</span>
                  </p>
                </div>
              </div>
              <div v-else class="card-body">
                <p class="text-muted">{{ t('news.emptyMessage') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import {mapState, mapActions} from 'vuex';

export default {
  name: 'NewsView',
  setup() {
    const { t, locale } = useI18n()
    return { t, locale }
  },
  data() {
    return {
      debugMode: true // Set to true to show debug information
    };
  },
  computed: {
    ...mapState('news', ['newsItems', 'loading', 'error']),
    recentItems() {
      if (!this.newsItems || !Array.isArray(this.newsItems) || this.newsItems.length === 0) {
        return [];
      }

      return [...this.newsItems]
          .sort((a, b) => new Date(b.created_on) - new Date(a.created_on))
          .slice(0, 5);
    }
  },
  methods: {
    ...mapActions('news', ['fetchNewsItems']),
    formatDate(dateString) {
      if (!dateString) return 'Unknown date';
      try {
        const options = {year: 'numeric', month: 'long', day: 'numeric'};
        return new Date(dateString).toLocaleDateString(this.$i18n.locale, options);
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
      }
    },
    retryFetch() {
      console.log('Retrying fetch of news items...');
      this.fetchNewsItems().catch(error => {
        console.error('Error retrying news items fetch:', error);
      });
    },
    forceRefresh() {
      console.log('Forcing refresh of news items...');
      this.fetchNewsItems().then(() => {
        console.log('Refreshed news items:', this.newsItems);
      }).catch(error => {
        console.error('Error refreshing news items:', error);
      });
    },
    getTranslatedTitle(item) {
      const currentLanguage = this.$i18n.locale;
      if (currentLanguage !== item.language) {
        const translation = item.translations?.find(t => t.language === currentLanguage);
        if (translation) {
          return translation.title;
        }
      }
      return item.title;
    }
  },
  created() {
    console.log('NewsView component created');
    this.fetchNewsItems().then(() => {
      console.log('News items fetched successfully, count:', this.newsItems.length);
    }).catch(error => {
      console.error('Error fetching news items:', error);
    });
  }
}
</script>

<style scoped>
.text-blog-title {
  font-size: 2.5rem;
  font-weight: 700;
}

.card {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: var(--ct-dark-tertiary);
}

.card-header {
  background-color: var(--ct-dark);
  color: white;
}

.card-body {
  color: #e0e0e0;
}

.card-title {
  color: white;
}

.card-text {
  color: #e0e0e0;
}

.btn-ctl {
  background-color: var(--ct-orange);
  color: #000;
  font-weight: 600;
}

.btn-ctl:hover {
  background-color: var(--ct-orange-hover);
}

/* For content */
.card-text :deep(img) {
  max-width: 100%;
  height: auto;
}

.card-text :deep(table) {
  width: 100%;
  margin-bottom: 1rem;
  border-collapse: collapse;
}

.card-text :deep(td), .card-text :deep(th) {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
}

.text-white {
  color: #ffffff !important;
}
</style>